import { useState, useEffect } from "react";
import { H1Title } from "../../components/typography/typography";
import { AppButton } from "../../components/buttons/button";
import AuthGeneralInput from "../../components/input/authinput";
import api from "../../services/api";
import { Success, Error as ToastError } from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";

const ROLE_STYLE = {
  participant: 'bg-primary-bg text-primary',
  judge: 'bg-orange-500/10 text-orange-dark',
  admin: 'bg-red-500/10 text-red-500',
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('participant');
  const [pagination, setPagination] = useState({ total: 0 });
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', firstName: '', lastName: '', role: 'judge', password: '' });
  const [creating, setCreating] = useState(false);
  const [issuedCredential, setIssuedCredential] = useState(null);

  useEffect(() => { loadUsers(); }, [role]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/users', { params: { role } });
      setUsers(data.data.users || []);
      setPagination({ total: data.data.total });
    } catch (err) {
      ToastError('Failed to load users');
    } finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...createForm };
      if (!payload.password) delete payload.password; // let server generate one
      if (payload.role === 'admin') payload.confirmAdmin = true;
      const { data } = await api.post('/admin/users', payload);
      const tempPassword = data?.data?.temporaryPassword;
      Success('User created');
      setShowCreate(false);
      setCreateForm({ email: '', firstName: '', lastName: '', role: 'judge', password: '' });
      if (tempPassword) {
        setIssuedCredential({ email: data.data.user?.email, password: tempPassword });
      }
      loadUsers();
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to create');
    } finally { setCreating(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const payload = { role: newRole };
      if (newRole === 'admin') payload.confirmAdmin = true;
      await api.put(`/admin/users/${userId}/role`, payload);
      Success('Role updated');
      loadUsers();
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed');
    }
  };

  const handleStatusChange = async (userId, status) => {
    try {
      await api.put(`/admin/users/${userId}/status`, { status });
      Success('Status updated');
      loadUsers();
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <H1Title size="sm">User Management</H1Title>
        <AppButton onClick={() => setShowCreate(!showCreate)} variant="outline" size="sm" fullWidth={false} className="px-4">
          <FontAwesomeIcon icon={faUserPlus} className="mr-1" /> Create User
        </AppButton>
      </div>

      {/* Issued credential modal — shown once when the server auto-generates a password. */}
      {issuedCredential && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setIssuedCredential(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border border-grey-200 max-w-md w-full p-6 space-y-4">
            <h3 className="font-bold text-lg">Temporary credentials</h3>
            <p className="text-sm text-grey-667">This password is shown once. Copy it now and share it with the new user out-of-band.</p>
            <div className="space-y-2 text-sm">
              <div><span className="text-grey-667">Email:</span> <span className="font-mono">{issuedCredential.email}</span></div>
              <div className="flex items-center gap-2">
                <span className="text-grey-667">Password:</span>
                <code className="bg-grey-bg px-2 py-1 rounded font-mono break-all">{issuedCredential.password}</code>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <AppButton variant="outline" fullWidth={false} className="px-4" onClick={() => {
                navigator.clipboard?.writeText(issuedCredential.password);
                Success('Copied to clipboard');
              }}>Copy</AppButton>
              <AppButton fullWidth={false} className="px-4" onClick={() => setIssuedCredential(null)}>Done</AppButton>
            </div>
          </div>
        </div>
      )}

      {/* Create Form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-grey-200 p-5 space-y-3">
          <h3 className="font-bold">Create Judge or Admin Account</h3>
          <div className="grid grid-cols-2 gap-3">
            <AuthGeneralInput label="First Name" name="firstName" value={createForm.firstName} change={(e) => setCreateForm({ ...createForm, firstName: e.target.value })} />
            <AuthGeneralInput label="Last Name" name="lastName" value={createForm.lastName} change={(e) => setCreateForm({ ...createForm, lastName: e.target.value })} />
          </div>
          <AuthGeneralInput label="Email" type="email" name="email" value={createForm.email} change={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
          <div>
            <AuthGeneralInput label="Temporary Password (optional)" type="text" name="password" value={createForm.password} change={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
            <p className="text-xs text-grey-667 mt-1">Leave blank to auto-generate a strong password — it will be shown once after creation.</p>
          </div>
          <div>
            <label className="block text-xs md:text-sm mb-2">Role</label>
            <select value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })} className="w-full border border-grey-200 rounded-md px-4 py-3 text-sm outline-none">
              <option value="judge">Judge</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex gap-2">
            <AppButton type="button" variant="outline" onClick={() => setShowCreate(false)} fullWidth={false} className="px-4">Cancel</AppButton>
            <AppButton type="submit" fullWidth={false} className="px-4" loading={creating}>Create</AppButton>
          </div>
        </form>
      )}

      {/* Role Filter */}
      <div className="flex gap-2">
        {['participant', 'judge', 'admin'].map(r => (
          <button
            key={r}
            onClick={() => setRole(r)}
            className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
              role === r ? 'bg-primary text-white' : 'bg-grey-bg text-grey-667 hover:bg-grey-200'
            }`}
          >
            {r.charAt(0).toUpperCase() + r.slice(1)}s
          </button>
        ))}
        <span className="self-center text-sm text-grey-98 ml-2">{pagination.total} total</span>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-grey-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-grey-667">Loading...</div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-grey-667">No {role}s found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-grey-bg border-b border-grey-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Joined</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-grey-200">
              {users.map(u => (
                <tr key={u._id} className="hover:bg-grey-bg/50">
                  <td className="px-4 py-3 font-semibold">{u.firstName} {u.lastName}</td>
                  <td className="px-4 py-3 text-grey-667">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${ROLE_STYLE[u.role]}`}>{u.role}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs ${u.status === 'active' ? 'text-green' : 'text-red-500'}`}>{u.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-grey-98">{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <select
                      value={u.status}
                      onChange={(e) => handleStatusChange(u._id, e.target.value)}
                      className="text-xs border border-grey-200 rounded px-2 py-1 outline-none"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
